import { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import type { EnginConsommationGroupRow } from '@/types';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const COLORS = [
  'rgb(20, 184, 166)',
  'rgb(59, 130, 246)',
  'rgb(168, 85, 247)',
  'rgb(236, 72, 153)',
  'rgb(249, 115, 22)',
  'rgb(34, 197, 94)',
  'rgb(234, 179, 8)',
  'rgb(239, 68, 68)',
];

function getColor(i: number) {
  return COLORS[i % COLORS.length];
}

interface EnginLineChartProps {
  data: EnginConsommationGroupRow[];
}

export default function EnginLineChart({ data }: EnginLineChartProps) {
  const chartData = useMemo(() => {
    const xDates: string[] = [];
    data.forEach((item) => {
      (item.enginAlimentation ?? []).forEach((dd) => {
        const d = dd.date;
        if (d && !xDates.includes(d)) xDates.push(d);
      });
    });
    xDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    const datasets = data.map((enginRow, idx) => {
      const alimentation = enginRow.enginAlimentation ?? [];
      let lastConsommation = 0;
      const values: number[] = [];
      let oldDateIdx = -1;
      for (const item of alimentation) {
        const dateStr = item.date;
        const idxDate = dateStr ? xDates.indexOf(dateStr) : -1;
        const cons = Number(item.consommation) ?? 0;
        if (idxDate !== -1) {
          for (let i = oldDateIdx + 1; i < idxDate; i++) {
            values[i] = lastConsommation;
          }
          values[idxDate] = cons;
          lastConsommation = cons;
          oldDateIdx = idxDate;
        }
      }
      for (let i = oldDateIdx + 1; i < xDates.length; i++) {
        values[i] = lastConsommation;
      }
      return {
        label: enginRow.engin?.code ?? `Engin ${idx + 1}`,
        data: values,
        fill: false,
        borderColor: getColor(idx),
        tension: 0.5,
      };
    });

    return {
      labels: xDates.map((d) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })),
      datasets,
    };
  }, [data]);

  const options = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { display: true, position: 'top' as const },
        title: { display: true, text: 'Consommations par jour' },
      },
      scales: {
        y: { beginAtZero: true },
      },
    }),
    []
  );

  if (!data.length) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center text-slate-600">
        Aucune donnée pour le graphique
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <Line data={chartData} options={options} />
    </div>
  );
}
